import {useQuery} from '@tanstack/react-query';
import React, {useContext} from 'react';
import {useParams} from 'react-router-dom';
import {TEVService, resourceTypeFormatting} from './tevapi/io';
import {Col, Row, Space, Tabs, Typography} from 'antd';

import UserContext from "./contexts/user";
import {LoadingAnimation} from './components/loading-page';

import { PolymorphicItem } from './tevapi/interfaces';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ItemCrumbs} from './components/detailed-item/item-crumbs';
import {ItemTable} from './components/detailed-item/field-table';
import FileViewer from './components/file-viewer';
import {ItemTimeline} from './components/detailed-item/timeline';
import {TOIDesignation} from './components/toi/toi';
import {ItemForm} from './components/forms/item-form';
import {ItemVetHistory} from './components/vetting/history';
import {ComparingStoreContext} from './contexts/items';
import {RESOURCETOHELPLOOKUP} from './components/help/item-data-docs';

const TabularItem: React.FC<{item: PolymorphicItem}> = ({item}) => {

  const user = useContext(UserContext);
  const tabData = [
    {
      key: "overview",
      label: "Overview",
      icon: <FontAwesomeIcon icon="fa-regular fa-table" />,
        children: (
          <div className="scrollbar-tab">
            <TOIDesignation item={item} />
            <ItemTable item={item} />
          </div>
      )
    },
    {
      key: "timeline",
      label: "Timeline",
      icon: <FontAwesomeIcon icon="fa-regular fa-timeline" />,
        children: (
          <div className="scrollbar-tab">
            <ItemTimeline item={item} />
          </div>
      ),
    },
  ];

  if (user.is_staff) {
    // Render admin interface
    tabData.push({
      key: "edit",
      label: "Edit",
      icon: <FontAwesomeIcon icon="fa-solid fa-pen-to-square" />,
        children: <div className='scrollbar-tab'>
          <ItemForm item={item} />
        </div>,
    });
  }

  return <Tabs
    items={tabData}
    defaultActiveKey='overview'
  />;
};

const RightSide: React.FC<{item: PolymorphicItem}> = ({item}) => {
  const user = useContext(UserContext);

  const tabData = [
    {
      key: "fileviewer",
      label: "File View",
      children: (
        <div className="scrollbar-tab">
          <FileViewer />
        </div>
      )
    }
  ];

  if (user.is_staff) {
    tabData.push({
      key: "vets",
      label: "Vets",
      children: (
        <div classname="scrollbar-tab">
          <ItemVetHistory item={item} />
        </div>
      ),
    });
  }

  return (
    <Tabs
      items={tabData}
      defaultActiveKey='fileviewer'
    />
  );
};

export const DetailedItem: React.FC = () => {
  const { pk } = useParams();

  const query = useQuery({
    queryKey: ["items", pk],
    queryFn: async(): Promise<PolymorphicItem> => {
      const response = await TEVService.get(`/itemdata/${pk}/`);
      return response.data;
    },
    refetchOnWindowFocus: false
  });

  if (query.isLoading) {
    return <LoadingAnimation />;
  }

  if (query.data === undefined || pk === undefined) {
    return "error";
  }

  return (
    <ComparingStoreContext pk={Number.parseInt(pk)}>
      <Row>
        <Col>
          <Space>
            <Typography.Title level={1}>
              {resourceTypeFormatting(query.data.resourcetype)}
            </Typography.Title>
            {RESOURCETOHELPLOOKUP[query.data.resourcetype]}
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={12} style={{maxHeight: "100vh"}}>
          <ItemCrumbs item={query.data} />
          <TabularItem item={query.data} />
        </Col>
        <Col span={12} style={{maxHeight: "100vh"}}>
          <RightSide item={query.data} />
        </Col>
      </Row>
    </ComparingStoreContext>
  );
};
